'use client'

import {createContext, startTransition, useContext, useEffect, useRef, useState} from 'react'
import {usePathname} from 'next/navigation'
import {useStudioIdentity} from '../../hooks/useStudioIdentity'
import {resolvePartnerSlug} from '../../lib/partner-slug'
import {JourneyContext, setJourneyActiveStudioScope, setJourneyContext, trackJourneyStep} from '../../lib/journey-logger'
import {isEmbedSourceVisit} from '../client/public-entry/publicEntryLinks'

const PENDING: JourneyContext = {scope: 'pending', scopeKey: null, actorType: 'customer', partnerId: null, partnerName: null}
const Context = createContext<JourneyContext>(PENDING)
export const useJourneyContext = (): JourneyContext => useContext(Context)

export function JourneyContextProvider({children}: {children: React.ReactNode}) {
    const activeSlug = resolvePartnerSlug()
    const activeScopeKey = activeSlug ? `studio:${activeSlug}` : null
    // This synchronous guard closes the interval before effects reset stale identity state.
    setJourneyActiveStudioScope(activeScopeKey)
    const identity = useStudioIdentity('Studio')
    const pathname = usePathname()
    const scopeKey = activeScopeKey && identity.slug === activeSlug && identity.partnerId && identity.displayName ? activeScopeKey : null
    const [context, setContext] = useState<JourneyContext>(PENDING)
    const emitted = useRef<string | null>(null)

    useEffect(() => {
        const next: JourneyContext = scopeKey
            ? {scope: 'studio', scopeKey, actorType: 'customer', partnerId: identity.partnerId, partnerName: identity.displayName}
            : PENDING
        emitted.current = null
        setJourneyContext(next)
        // A transition waits for Suspense boundaries that are still hydrating instead of replacing them with their fallback.
        startTransition(() => setContext(next))
    }, [identity.displayName, identity.partnerId, scopeKey])

    useEffect(() => {
        if (context.scope !== 'studio' || !pathname || emitted.current === `${context.scopeKey}:${pathname}`) return
        emitted.current = `${context.scopeKey}:${pathname}`
        trackJourneyStep({feature: 'navigation', action: 'route_view', stage: 'viewed', outcome: 'none', measurement: 'none', metricOwner: 'none', route: pathname})
        // A link from the Studio's website widget: the widget itself sends nothing, as its cookies are third-party.
        if (isEmbedSourceVisit()) {
            trackJourneyStep({feature: 'public_embed', action: 'link', stage: 'opened', outcome: 'success', measurement: 'feature_usage', metricOwner: 'frontend', route: pathname})
        }
    }, [context, pathname])

    return <Context.Provider value={context}>{children}</Context.Provider>
}
